import { useState, useEffect } from 'react';
import {
  CheckCircle,
  Circle,
  XCircle,
  Download,
  FileImage,
  FileCode,
  Loader2,
  Clock,
} from 'lucide-react';
import type { ExportJob, ExportJobStatus } from '../../../types/export';

interface ExportProgressPanelProps {
  job: ExportJob | null;
  onDownloadBackground: () => void;
  onDownloadOverlay: () => void;
  onDownloadComposite?: () => void;
}

const STAGES: { key: ExportJobStatus; label: string }[] = [
  { key: 'pending', label: 'Preparing' },
  { key: 'resolving', label: 'Resolving Fields' },
  { key: 'rendering', label: 'Rendering Background' },
  { key: 'generating', label: 'Generating Packet' },
  { key: 'uploading', label: 'Uploading Files' },
  { key: 'complete', label: 'Complete' },
];

function getStageIndex(status: ExportJobStatus): number {
  if (status === 'failed') return -1;
  return STAGES.findIndex((s) => s.key === status);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  const remainder = ms % 1000;
  if (seconds < 60) return `${seconds}.${Math.floor(remainder / 100)}s`;
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}m ${secs}s`;
}

export function ExportProgressPanel({
  job,
  onDownloadBackground,
  onDownloadOverlay,
  onDownloadComposite,
}: ExportProgressPanelProps) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!job || job.progress.status === 'complete' || job.progress.status === 'failed') return;

    const startTime = job.startedAt.getTime();
    const interval = setInterval(() => {
      setElapsed(Date.now() - startTime);
    }, 100);

    return () => clearInterval(interval);
  }, [job]);

  useEffect(() => {
    if (job?.completedAt && job?.startedAt) {
      setElapsed(job.completedAt.getTime() - job.startedAt.getTime());
    }
  }, [job?.completedAt, job?.startedAt]);

  if (!job) {
    return (
      <div className="flex items-center justify-center py-12 text-white/40">
        <Loader2 size={24} className="animate-spin" />
      </div>
    );
  }

  const currentIndex = getStageIndex(job.progress.status);
  const isFailed = job.progress.status === 'failed';
  const isComplete = job.progress.status === 'complete';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-center gap-2 text-white/40 text-sm">
        <Clock size={14} />
        <span>{formatDuration(elapsed)}</span>
      </div>

      <div className="relative h-2 rounded-full bg-[#12121a] overflow-hidden">
        <div
          className={`absolute inset-y-0 left-0 rounded-full transition-all duration-500 ease-out ${
            isFailed ? 'bg-red-500' : isComplete ? 'bg-emerald-500' : 'bg-cyan-500'
          }`}
          style={{ width: `${job.progress.percent}%` }}
        />
        {!isComplete && !isFailed && (
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-cyan-400/30 animate-pulse"
            style={{ width: `${Math.min(job.progress.percent + 5, 100)}%` }}
          />
        )}
      </div>

      <div className="text-center">
        <span
          className={`text-2xl font-bold font-['BioRhyme'] ${
            isFailed ? 'text-red-400' : isComplete ? 'text-emerald-400' : 'text-white'
          }`}
        >
          {job.progress.percent}%
        </span>
      </div>

      <div className="space-y-1">
        {STAGES.map((stage, index) => {
          const isActive = index === currentIndex;
          const isCompleted = !isFailed && currentIndex > index;
          const isFailedStage = isFailed && index === Math.max(currentIndex, 0);

          let Icon = Circle;
          let iconColor = 'text-white/20';
          let textColor = 'text-white/30';

          if (isCompleted || (isComplete && index === STAGES.length - 1)) {
            Icon = CheckCircle;
            iconColor = 'text-emerald-500';
            textColor = 'text-emerald-400';
          } else if (isActive && !isFailed) {
            Icon = Loader2;
            iconColor = 'text-cyan-400';
            textColor = 'text-white';
          } else if (isFailedStage) {
            Icon = XCircle;
            iconColor = 'text-red-500';
            textColor = 'text-red-400';
          }

          return (
            <div
              key={stage.key}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                isActive && !isFailed ? 'bg-cyan-500/5' : ''
              } ${isFailedStage ? 'bg-red-500/5' : ''}`}
            >
              <Icon
                size={18}
                className={`${iconColor} ${isActive && !isFailed ? 'animate-spin' : ''} flex-shrink-0`}
              />
              <span className={`text-sm ${textColor}`}>{stage.label}</span>
              {isActive && !isFailed && (
                <span className="ml-auto text-xs text-white/30">{job.progress.message}</span>
              )}
              {isFailedStage && (
                <span className="ml-auto text-xs text-red-400 truncate max-w-[200px]">
                  {job.progress.error}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {isComplete && job.outputs && (
        <div className="pt-4 border-t border-white/[0.06] space-y-2">
          <div className="text-xs font-medium text-white/40 uppercase tracking-wider mb-3">
            Downloads
          </div>

          {job.outputs.backgroundBlob && (
            <button
              onClick={onDownloadBackground}
              className="w-full flex items-center gap-3 p-3 rounded-lg bg-[#12121a] border border-white/[0.06] hover:bg-white/[0.06] transition-colors group"
            >
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <FileImage size={16} className="text-blue-400" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-sm text-white group-hover:text-cyan-400 transition-colors">
                  Background Image
                </div>
                <div className="text-xs text-white/40">
                  {job.outputs.dimensions.width} x {job.outputs.dimensions.height}px
                  {job.outputs.fileSizes.background &&
                    ` · ${formatBytes(job.outputs.fileSizes.background)}`}
                </div>
              </div>
              <Download
                size={16}
                className="text-white/30 group-hover:text-cyan-400 transition-colors"
              />
            </button>
          )}

          {job.outputs.overlayBlob && (
            <button
              onClick={onDownloadOverlay}
              className="w-full flex items-center gap-3 p-3 rounded-lg bg-[#12121a] border border-white/[0.06] hover:bg-white/[0.06] transition-colors group"
            >
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <FileCode size={16} className="text-emerald-400" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-sm text-white group-hover:text-cyan-400 transition-colors">
                  Overlay Packet
                </div>
                <div className="text-xs text-white/40">
                  JSON packet (.txt)
                  {job.outputs.fileSizes.overlay &&
                    ` · ${formatBytes(job.outputs.fileSizes.overlay)}`}
                </div>
              </div>
              <Download
                size={16}
                className="text-white/30 group-hover:text-cyan-400 transition-colors"
              />
            </button>
          )}

          {job.outputs.compositeBlob && onDownloadComposite && (
            <button
              onClick={onDownloadComposite}
              className="w-full flex items-center gap-3 p-3 rounded-lg bg-[#12121a] border border-white/[0.06] hover:bg-white/[0.06] transition-colors group"
            >
              <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <FileImage size={16} className="text-amber-400" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-sm text-white group-hover:text-cyan-400 transition-colors">
                  Composite Image
                </div>
                <div className="text-xs text-white/40">
                  Background + overlay merged
                  {job.outputs.fileSizes.composite &&
                    ` · ${formatBytes(job.outputs.fileSizes.composite)}`}
                </div>
              </div>
              <Download
                size={16}
                className="text-white/30 group-hover:text-cyan-400 transition-colors"
              />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
